import * as THREE from 'three';
import { getDildoColor } from './dildoGeometry';

export const vertexShader = `
varying vec3 vWorldPos;
varying vec3 vWorldNormal;
varying vec2 vUv;

void main() {
  vUv = uv;
  vec4 wp = modelMatrix * vec4(position, 1.0);
  vWorldPos = wp.xyz;
  vWorldNormal = normalize(mat3(modelMatrix) * normal);
  gl_Position = projectionMatrix * viewMatrix * wp;
}
`;

export const fragmentShader = `
precision highp float;

uniform vec3  uBaseColor;
uniform float uRoughness;
uniform float uSssStrength;
uniform float uVeins;
uniform vec3  uCameraPos;

uniform vec3  uKeyPos;
uniform vec3  uKeyColor;
uniform float uKeyInt;
uniform vec3  uFillPos;
uniform vec3  uFillColor;
uniform float uFillInt;
uniform vec3  uRimPos;
uniform vec3  uRimColor;
uniform float uRimInt;
uniform vec3  uBotPos;
uniform vec3  uBotColor;
uniform float uBotInt;

varying vec3 vWorldPos;
varying vec3 vWorldNormal;
varying vec2 vUv;

vec3 toLinear(vec3 c) { return pow(max(c, vec3(0.001)), vec3(2.2)); }
vec3 toSRGB(vec3 c)   { return pow(max(c, vec3(0.0)),   vec3(1.0/2.2)); }

float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p) {
  vec2 i = floor(p); vec2 f = fract(p);
  vec2 u = f*f*(3.0-2.0*f);
  return mix(mix(hash(i+vec2(0,0)), hash(i+vec2(1,0)), u.x),
             mix(hash(i+vec2(0,1)), hash(i+vec2(1,1)), u.x), u.y);
}
float fbm(vec2 p) {
  float v=0.0; float a=0.5;
  for (int i=0;i<4;i++) { v+=a*noise(p); p*=2.0; a*=0.5; }
  return v;
}

float phong(vec3 N, vec3 L, vec3 V, float rough) {
  vec3 H = normalize(L + V);
  return pow(max(0.0, dot(N,H)), mix(256.0, 4.0, rough));
}

void main() {
  vec3 N = normalize(vWorldNormal);
  vec3 V = normalize(uCameraPos - vWorldPos);

  vec3 baseColor = toLinear(uBaseColor);

  // Veins — procedural branching network along shaft (UV-based)
  if (uVeins > 0.01) {
    vec2 vWarp = vUv * vec2(1.0, 10.0);
    vWarp.x += fbm(vUv * vec2(2.0, 5.0)) * 0.12;
    vWarp.y += fbm(vUv * vec2(2.0, 5.0) + 1.7) * 0.12;
    float n1 = 1.0 - abs(noise(vWarp) * 2.0 - 1.0);
    float n2 = 1.0 - abs(noise(vWarp * vec2(1.5, 2.1) + 0.9) * 2.0 - 1.0);
    float vein = smoothstep(0.70, 0.94, n1 * n2);
    // Shade veins slightly deeper/cooler than base
    vec3 veinCol = baseColor * 0.55 + vec3(0.01, 0.02, 0.10) * uVeins;
    baseColor = mix(baseColor, veinCol, vein * uVeins * 0.55);
  }

  // Slightly darker in valleys / concavities (ambient occlusion approximation via normal)
  float ao = 0.85 + 0.15 * max(0.0, dot(N, vec3(0,1,0)));
  baseColor *= ao;

  vec3 col = vec3(0.0);

  // Key light — warm, upper-front
  vec3 L1 = normalize(uKeyPos - vWorldPos);
  float NL1 = dot(N, L1);
  float diff1 = max(0.0, (NL1 + 0.25) / 1.25);
  float spec1 = phong(N, L1, V, uRoughness) * 0.7 * (1.0 - uRoughness * 0.65);
  col += baseColor * diff1 * toLinear(uKeyColor) * uKeyInt;
  col += vec3(1.0, 0.97, 0.94) * spec1 * uKeyInt;

  // SSS — warm back-scatter glow
  float sss = max(0.0, -NL1 + 0.2) * uSssStrength;
  col += baseColor * vec3(1.4, 0.45, 0.2) * sss * uKeyInt * 0.3;

  // Fill light — cool blue from right side
  vec3 L2 = normalize(uFillPos - vWorldPos);
  float diff2 = max(0.0, dot(N, L2));
  float spec2 = phong(N, L2, V, uRoughness) * 0.25 * (1.0 - uRoughness * 0.5);
  col += baseColor * diff2 * toLinear(uFillColor) * uFillInt;
  col += vec3(0.8, 0.9, 1.0) * spec2 * uFillInt;

  // Rim / back light — neon cyan edge glow
  vec3 L3 = normalize(uRimPos - vWorldPos);
  float rimFresnel = pow(1.0 - max(0.0, dot(N, V)), 2.8);
  float rimDiff = max(0.0, dot(N, L3));
  col += toLinear(uRimColor) * rimFresnel * rimDiff * uRimInt * 0.75;

  // Bottom fill — warm bounce from below
  vec3 L4 = normalize(uBotPos - vWorldPos);
  float diff4 = max(0.0, dot(N, L4));
  col += baseColor * diff4 * toLinear(uBotColor) * uBotInt;

  // Ambient
  col += baseColor * 0.12 * vec3(0.7, 0.8, 1.0);

  // Filmic tone map + gamma
  col = col / (col + vec3(0.85));
  col = toSRGB(col);

  gl_FragColor = vec4(col, 1.0);
}
`;

export function createDildoMaterial(params) {
  const [r, g, b] = getDildoColor(params.colorTone);

  return new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms: {
      uBaseColor:   { value: new THREE.Color(r, g, b) },
      uRoughness:   { value: params.roughness },
      uSssStrength: { value: params.sssStrength },
      uVeins:       { value: params.veins },
      uCameraPos:   { value: new THREE.Vector3(0, 0.3, 5) },
      // Key light — warm, upper-front-left
      uKeyPos:   { value: new THREE.Vector3(-2.5, 4.5, 3.5) },
      uKeyColor: { value: new THREE.Color(1.0, 0.94, 0.88) },
      uKeyInt:   { value: 2.4 },
      // Fill light — cool, right side
      uFillPos:   { value: new THREE.Vector3(3.5, 1.0, 2.5) },
      uFillColor: { value: new THREE.Color(0.4, 0.55, 0.95) },
      uFillInt:   { value: 0.9 },
      // Rim light — neon cyan, behind-low
      uRimPos:   { value: new THREE.Vector3(-1.5, -0.5, -4.5) },
      uRimColor: { value: new THREE.Color(0.0, 0.85, 1.0) },
      uRimInt:   { value: 1.8 },
      // Bottom bounce
      uBotPos:   { value: new THREE.Vector3(0, -5, 1.5) },
      uBotColor: { value: new THREE.Color(0.9, 0.7, 0.55) },
      uBotInt:   { value: 0.35 },
    },
    side: THREE.DoubleSide,
  });
}
